import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFileContract,
  FaUserCheck,
  FaStethoscope,
  FaCreditCard,
  FaShieldAlt,
  FaExclamationTriangle,
  FaBan,
  FaHistory,
  FaEdit,
  FaDownload,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGavel
} from 'react-icons/fa';

const TermsofService = () => {
  const [currentSection, setCurrentSection] = useState('acceptance');
  const [lastUpdated] = useState('January 15, 2024');

  const sections = [
    { id: 'acceptance', title: 'Acceptance of Terms' },
    { id: 'services', title: 'Services Offered' },
    { id: 'responsibilities', title: 'User Responsibilities' },
    { id: 'payments', title: 'Payments & Fees' },
    { id: 'refunds', title: 'Refunds & Cancellations' },
    { id: 'availability', title: 'Service Availability' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'changes', title: 'Changes to Terms' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setCurrentSection(sectionId);
    }
  };

  const downloadTerms = () => {
    // In a real app, this would download a PDF version
    alert('PDF version of Terms of Service will be downloaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-3">Terms of Service</h1>
              <p className="text-amber-100 text-lg">
                Legal agreement between you and Anusaya Healthcare
              </p>
            </div>
            <div className="mt-6 md:mt-0 flex items-center space-x-4">
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">
                Effective: {lastUpdated}
              </span>
              <button
                onClick={downloadTerms}
                className="bg-white text-amber-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
              >
                <FaDownload className="mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Warning Banner */}
        <div className="mb-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-r-lg">
          <div className="flex items-start">
            <FaExclamationTriangle className="text-amber-500 text-xl mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-amber-800">Important Legal Notice</h3>
              <p className="text-amber-700 text-sm mt-1">
                These Terms of Service constitute a legally binding agreement. Please read them carefully 
                before using our services. By accessing or using our services, you agree to be bound by these terms.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <div className="flex items-center mb-6">
                <FaGavel className="text-amber-600 text-xl mr-3" />
                <h2 className="text-xl font-bold text-gray-800">Terms Overview</h2>
              </div>
              
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                      currentSection === section.id
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${
                        currentSection === section.id ? 'bg-amber-600' : 'bg-gray-300'
                      }`}></div>
                      {section.title}
                    </div>
                  </button>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <div className="flex items-start">
                  <FaUserCheck className="text-amber-500 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-amber-800 mb-1">Your Agreement</h4>
                    <p className="text-sm text-amber-700">
                      By using our services, you accept all terms
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            {/* Acceptance of Terms */}
            <div id="acceptance" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-amber-100 rounded-xl mr-4">
                  <FaFileContract className="text-amber-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">1. Acceptance of Terms</h2>
                  <p className="text-gray-600 mt-1">Your Agreement to These Terms</p>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to <strong>Anusaya Healthcare</strong>. These Terms of Service ("Terms") govern 
                  your access to and use of our website, services, and applications (collectively, the "Services"). 
                  Please read these Terms carefully before accessing or using our Services.
                </p>
                
                <div className="p-4 bg-amber-50 rounded-xl border-l-4 border-amber-500">
                  <div className="flex items-start">
                    <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-1">By accessing or using our Services, you:</h4>
                      <ul className="text-amber-700 list-disc pl-5 space-y-1">
                        <li>Confirm that you are at least 18 years old</li>
                        <li>Agree to be bound by these Terms and our Privacy Policy</li>
                        <li>Agree to comply with all applicable laws and regulations</li>
                        <li>Accept responsibility for all activities under your account</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-red-50 rounded-xl">
                  <div className="flex items-start">
                    <FaTimesCircle className="text-red-500 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-1">If you do not agree to these Terms:</h4>
                      <p className="text-red-700">
                        You must not access or use our Services. Your continued use of the Services 
                        constitutes your acceptance of any changes or revisions to these Terms.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services Offered */}
            <div id="services" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  <FaStethoscope className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">2. Services Offered</h2>
                  <p className="text-gray-600 mt-1">What We Provide</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Anusaya Healthcare provides comprehensive healthcare services including but not limited to:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-blue-200 rounded-xl bg-blue-50">
                    <h4 className="font-bold text-blue-800 mb-2">Medical Services</h4>
                    <ul className="text-blue-700 space-y-1 text-sm">
                      <li>• Doctor consultations (online & in-person)</li>
                      <li>• Diagnostic tests and lab services</li>
                      <li>• Specialist referrals</li>
                      <li>• Prescription services</li>
                      <li>• Health check-up packages</li>
                    </ul>
                  </div>

                  <div className="p-4 border border-green-200 rounded-xl bg-green-50">
                    <h4 className="font-bold text-green-800 mb-2">Support Services</h4>
                    <ul className="text-green-700 space-y-1 text-sm">
                      <li>• Appointment scheduling</li>
                      <li>• Medical record management</li>
                      <li>• Emergency services coordination</li>
                      <li>• Billing and insurance support</li>
                      <li>• Patient education resources</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <FaInfoCircle className="text-gray-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Important Notes:</h4>
                    <ul className="text-gray-700 space-y-1">
                      <li>• Our services are for informational purposes and do not replace professional medical advice</li>
                      <li>• In case of medical emergencies, please contact emergency services immediately</li>
                      <li>• We reserve the right to modify or discontinue any service at any time</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* User Responsibilities */}
            <div id="responsibilities" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-green-100 rounded-xl mr-4">
                  <FaUserCheck className="text-green-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">3. User Responsibilities</h2>
                  <p className="text-gray-600 mt-1">Your Obligations</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 border border-green-200 rounded-xl">
                  <h4 className="font-bold text-green-800 mb-3 flex items-center">
                    <FaCheckCircle className="mr-2" />
                    Provide Accurate Information
                  </h4>
                  <p className="text-gray-700">
                    You agree to provide accurate, current, and complete information during registration 
                    and when using our Services. This includes but is not limited to:
                  </p>
                  <ul className="mt-2 text-gray-600 list-disc pl-5 space-y-1">
                    <li>Correct personal identification details</li>
                    <li>Accurate medical history information</li>
                    <li>Valid contact information</li>
                    <li>True and complete payment details</li>
                  </ul>
                </div>

                <div className="p-4 border border-red-200 rounded-xl">
                  <h4 className="font-bold text-red-800 mb-3 flex items-center">
                    <FaBan className="mr-2" />
                    Prohibited Activities
                  </h4>
                  <p className="text-gray-700 mb-2">
                    You agree NOT to:
                  </p>
                  <ul className="text-red-600 list-disc pl-5 space-y-1">
                    <li>Use the Services for any illegal purpose</li>
                    <li>Provide false or misleading information</li>
                    <li>Impersonate any person or entity</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with or disrupt the Services</li>
                    <li>Share your account credentials with others</li>
                    <li>Use automated systems to access our Services</li>
                    <li>Harass, threaten, or intimidate our staff or other users</li>
                  </ul>
                </div>

                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <h4 className="font-bold text-green-800 mb-2">Account Security</h4>
                  <p className="text-green-700">
                    You are responsible for maintaining the confidentiality of your account credentials 
                    and for all activities that occur under your account. You agree to immediately notify 
                    us of any unauthorized use of your account.
                  </p>
                </div>
              </div>
            </div>

            {/* Payments & Fees */}
            <div id="payments" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-purple-100 rounded-xl mr-4">
                  <FaCreditCard className="text-purple-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">4. Payments & Fees</h2>
                  <p className="text-gray-600 mt-1">Financial Terms and Conditions</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-purple-50 rounded-xl">
                  <h4 className="font-bold text-purple-800 mb-3 flex items-center">
                    <FaCreditCard className="mr-2" />
                    Payment Processing via Razorpay
                  </h4>
                  <p className="text-purple-700 mb-3">
                    All payments for our Services are securely processed through <strong>Razorpay</strong>, 
                    our authorized payment gateway partner. By making a payment, you agree to Razorpay's 
                    terms and conditions.
                  </p>
                  <a 
                    href="https://razorpay.com/terms/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Razorpay's Terms of Service →
                  </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-purple-200 rounded-xl">
                    <h4 className="font-bold text-purple-800 mb-2">Payment Confirmation</h4>
                    <ul className="text-purple-700 space-y-2 text-sm">
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        Confirmation email sent immediately
                      </li>
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        Invoice available in your account
                      </li>
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        SMS notification for successful payments
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border border-purple-200 rounded-xl">
                    <h4 className="font-bold text-purple-800 mb-2">Accepted Payment Methods</h4>
                    <ul className="text-purple-700 space-y-1 text-sm">
                      <li>• Credit/Debit Cards</li>
                      <li>• Net Banking</li>
                      <li>• UPI (Google Pay, PhonePe, etc.)</li>
                      <li>• Digital Wallets</li>
                      <li>• EMI Options</li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-xl">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-red-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-1">Important Payment Information</h4>
                      <p className="text-red-700">
                        Prices are in Indian Rupees (₹) and include applicable taxes. We reserve the right 
                        to change our prices at any time, but such changes will not affect completed transactions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Refunds & Cancellations */}
            <div id="refunds" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-teal-100 rounded-xl mr-4">
                  <FaHistory className="text-teal-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">5. Refunds & Cancellations</h2>
                  <p className="text-gray-600 mt-1">Cancellation and Refund Policy</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 border border-teal-200 rounded-xl">
                    <h4 className="font-bold text-teal-800 mb-3">Appointment Cancellations</h4>
                    <ul className="text-teal-700 space-y-2">
                      <li className="flex items-start">
                        <span className="font-bold mr-2">24+ Hours:</span>
                        <span>Full refund or reschedule</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold mr-2">4-24 Hours:</span>
                        <span>50% refund or reschedule</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold mr-2">Less than 4 Hours:</span>
                        <span>No refund, rescheduling may incur additional fees</span>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border border-teal-200 rounded-xl">
                    <h4 className="font-bold text-teal-800 mb-3">Service Cancellations</h4>
                    <ul className="text-teal-700 space-y-2">
                      <li className="flex items-start">
                        <span className="font-bold mr-2">Lab Tests:</span>
                        <span>Full refund if cancelled before sample collection</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold mr-2">Health Packages:</span>
                        <span>Refund based on services not utilized</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold mr-2">Emergency:</span>
                        <span>No cancellation, immediate care provided</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-teal-50 rounded-xl">
                  <h4 className="font-bold text-teal-800 mb-2">Refund Processing</h4>
                  <p className="text-teal-700">
                    Approved refunds will be processed within <strong>7-10 business days</strong> and 
                    credited to the original payment method. Processing times may vary based on your 
                    bank or payment provider.
                  </p>
                </div>

                <div className="p-4 bg-amber-50 rounded-xl">
                  <div className="flex items-start">
                    <FaInfoCircle className="text-amber-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-1">Non-Refundable Services</h4>
                      <p className="text-amber-700">
                        The following services are generally non-refundable: emergency consultations, 
                        services already rendered, and consumable medical supplies.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Availability */}
            <div id="availability" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  <FaShieldAlt className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">6. Service Availability & Limitations</h2>
                  <p className="text-gray-600 mt-1">What We Can and Cannot Guarantee</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-bold text-blue-800 mb-2">Service Hours</h4>
                  <p className="text-blue-700 mb-2">
                    Our Services are generally available:
                  </p>
                  <ul className="text-blue-700 list-disc pl-5 space-y-1">
                    <li><strong>Regular Hours:</strong> 8:00 AM - 10:00 PM (All days)</li>
                    <li><strong>Emergency Services:</strong> 24/7 Available</li>
                    <li><strong>Online Consultations:</strong> Based on doctor availability</li>
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-2">Service Limitations</h4>
                  <p className="text-gray-700">
                    While we strive to provide uninterrupted Services, we cannot guarantee that:
                  </p>
                  <ul className="mt-2 text-gray-600 list-disc pl-5 space-y-1">
                    <li>The Services will be available at all times</li>
                    <li>The Services will be free from errors or interruptions</li>
                    <li>All features will work on all devices or browsers</li>
                    <li>Third-party integrations will always function properly</li>
                  </ul>
                </div>

                <div className="p-4 bg-red-50 rounded-xl">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-red-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-1">Emergency Situations</h4>
                      <p className="text-red-700">
                        Our online Services are NOT for medical emergencies. In case of emergency, 
                        please call emergency services (108/112) or visit the nearest hospital immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div id="liability" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-red-100 rounded-xl mr-4">
                  <FaExclamationTriangle className="text-red-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">7. Limitation of Liability</h2>
                  <p className="text-gray-600 mt-1">What We Are Responsible For</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 border border-red-200 rounded-xl">
                  <h4 className="font-bold text-red-800 mb-2">To the fullest extent permitted by law:</h4>
                  <ul className="text-red-700 space-y-2">
                    <li className="flex items-start">
                      <FaTimesCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                      <span>We are not liable for any indirect, incidental, or consequential damages</span>
                    </li>
                    <li className="flex items-start">
                      <FaTimesCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                      <span>We are not responsible for third-party services or content</span>
                    </li>
                    <li className="flex items-start">
                      <FaTimesCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                      <span>We are not liable for data loss or system failures</span>
                    </li>
                    <li className="flex items-start">
                      <FaTimesCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Our total liability shall not exceed the amount you paid for the specific service</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-50 rounded-xl">
                  <h4 className="font-bold text-yellow-800 mb-2">Medical Disclaimer</h4>
                  <p className="text-yellow-700">
                    Our Services provide healthcare information and facilitate access to medical professionals. 
                    We do not provide medical advice, diagnosis, or treatment. Always seek the advice of 
                    qualified healthcare providers with any questions regarding medical conditions.
                  </p>
                </div>
              </div>
            </div>

            {/* Changes to Terms */}
            <div id="changes" className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-indigo-100 rounded-xl mr-4">
                  <FaEdit className="text-indigo-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">8. Changes to Terms</h2>
                  <p className="text-gray-600 mt-1">How We Update These Terms</p>
                </div>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-4">
                  We reserve the right to modify these Terms at any time. When we make changes, 
                  we will update the "Effective Date" at the top of this page and may provide additional notice.
                </p>

                <div className="p-4 bg-indigo-50 rounded-xl mb-4">
                  <h4 className="font-bold text-indigo-800 mb-2">Notification of Changes</h4>
                  <p className="text-indigo-700">
                    For significant changes, we will:
                  </p>
                  <ul className="text-indigo-700 list-disc pl-5 mt-2 space-y-1">
                    <li>Post a prominent notice on our website</li>
                    <li>Send email notifications to registered users</li>
                    <li>Provide at least 30 days' notice for major changes</li>
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-2">Your Continued Use</h4>
                  <p className="text-gray-700">
                    Your continued use of our Services after any changes to these Terms constitutes 
                    your acceptance of the new Terms. If you do not agree to the modified Terms, 
                    you must stop using our Services.
                  </p>
                </div>

                {/* Contact Information */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                  <h4 className="font-bold text-blue-800 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-blue-700 flex items-center mb-2">
                        <FaEnvelope className="mr-2" />
                        <strong>Email:</strong> legal@Anusaya.com
                      </p>
                      <p className="text-blue-700 flex items-center mb-2">
                        <FaPhone className="mr-2" />
                        <strong>Phone:</strong> +91 98765 43210
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700 flex items-start mb-2">
                        <FaMapMarkerAlt className="mt-1 mr-2 flex-shrink-0" />
                        <strong>Address:</strong> 123 Medical Plaza, Health Street, Pune, Maharashtra - 411001
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 p-4 bg-white rounded-xl shadow border-t">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              These Terms of Service were last updated on {lastUpdated}. If you have any questions 
              about these Terms, please contact us using the information provided above.
            </p>
            <div className="mt-4 flex justify-center space-x-4">
              <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Privacy Policy
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/contact" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Contact Us
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/faq" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsofService;